import { BodyText, Heading, Modal } from '@farol-ds/react'
import { Image } from '@jusbrasil-web/shared-next-image'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { useEffect, useRef, useState } from 'react'

import validatingImgSrc from '../../../assets/id-document-validation/validation.svg'
import {
  GA4EventHandlers,
  INPUT_SUBMIT_EVENT_COMPONENT_PROPS,
  useGetProfileByCPF,
  useValidateCPF,
} from '../../../hooks'
import {
  DOCUMENT_VALIDATION_ERRORS,
  identifyDocumentValidationError,
  isDocumentValidationError,
} from '../../../utils'
import { useIdDocumentValidationContext } from '../context'
import { Steps } from '../enums'
import styles from './steps.module.scss'

interface ValidatingDocumentProps {
  validateCallback?: (isValidatedSuccess: boolean) => void
  gA4EventHandlers?: GA4EventHandlers
}

export function ValidatingDocument(props: ValidatingDocumentProps) {
  const { validateCallback, gA4EventHandlers } = props
  const {
    setCurrentStep,
    sharedData,
    setSharedData,
    viewer,
    eventData,
    validationOrigin,
    onAfterValidationSuccess,
  } = useIdDocumentValidationContext()
  const validateCPF = useValidateCPF()
  const getProfileByCPF = useGetProfileByCPF()
  const [loading, setLoading] = useState(false)
  const hasSubmittedRef = useRef(false)
  const pid = viewer?.pid

  useEffect(() => {
    sendMetrics('UserAccount.ValidatingIdViewed', `${pid}`, { ...eventData })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const cpf = sharedData.cpf || viewer?.cpf
    const birthDate =
      sharedData.birthDate ?? (viewer?.birthDate ? new Date(viewer.birthDate) : undefined)
    const { documentData, documentName } = sharedData

    if (
      loading ||
      hasSubmittedRef.current ||
      !cpf ||
      !birthDate ||
      !documentData ||
      !documentName
    ) {
      return
    }

    hasSubmittedRef.current = true
    setLoading(true)

    validateCPF({
      cpf,
      birthDate,
      documentData,
      documentName,
      matchValidationFirst: true,
      origin: validationOrigin,
    })
      .then(async () => {
        gA4EventHandlers?.sendInputSubmitComponent({
          ...INPUT_SUBMIT_EVENT_COMPONENT_PROPS.VALIDATE_IDENTITY_DOCUMENT_FILE_UPLOAD_CPF_VALIDATION_FORM_ATTACHMENT,
          successfulAttempt: true,
        })

        sendMetrics('UserAccount.IdValidationSuccessViewed', `${pid}`, { ...eventData })

        try {
          await onAfterValidationSuccess?.()
        } catch (error) {
          console.error('Error running onAfterValidationSuccess:', error)
        }

        validateCallback?.(true)
        setCurrentStep(Steps.IDENTITY_SUCCESS)
      })
      .catch(async (error) => {
        gA4EventHandlers?.sendInputSubmitComponent({
          ...INPUT_SUBMIT_EVENT_COMPONENT_PROPS.VALIDATE_IDENTITY_DOCUMENT_FILE_UPLOAD_CPF_VALIDATION_FORM_ATTACHMENT,
          successfulAttempt: false,
        })

        const { type: errorType, message: errorMessage } = identifyDocumentValidationError(error)

        if (isDocumentValidationError(errorType)) {
          setSharedData({
            ...sharedData,
            errorType,
            errorMessage,
          })
          setCurrentStep(Steps.DOCUMENT_ERROR)
          return
        }

        if (errorType === DOCUMENT_VALIDATION_ERRORS.CPF_ALREADY_IN_USE && cpf) {
          const response = await getProfileByCPF(cpf)
          const profile = response?.data?.root?.profileByValidatedCpf

          if (profile) {
            setSharedData({
              ...sharedData,
              profileInUse: {
                pid: profile.pid,
                email: profile.user?.obfuscatedEmail ?? '',
              },
            })
            setCurrentStep(Steps.CPF_IN_USE)
            return
          }

          console.error('Error getting current profile using the cpf')
        }

        setCurrentStep(Steps.IDENTITY_ERROR)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [
    sharedData,
    viewer,
    loading,
    validateCPF,
    getProfileByCPF,
    setCurrentStep,
    setSharedData,
    validationOrigin,
    gA4EventHandlers,
    validateCallback,
    onAfterValidationSuccess,
    eventData,
    pid,
  ])

  return (
    <Modal.Body className={styles.body} data-testid="validating-document-step">
      <Image
        alt="Verificando identidade..."
        src={validatingImgSrc}
        height={200}
        width={200}
        className={styles.image}
      />
      <Heading size="lg" className={styles.title}>
        Verificando identidade...
      </Heading>
      <BodyText size="lg" className={styles.text}>
        Aguarde enquanto validamos as informações
      </BodyText>
    </Modal.Body>
  )
}
